import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section with Arabic-inspired pattern */}
      <section className="relative py-32 bg-gradient-to-br from-emerald-900 via-emerald-800 to-emerald-900 overflow-hidden">
        {/* Arabic-inspired geometric pattern overlay */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C9A84C' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center text-cream-100 max-w-4xl mx-auto">
            <div className="mb-6">
              <div className="w-20 h-1 bg-gold-500 mx-auto mb-8"></div>
            </div>
            <h1 className="text-6xl font-bold mb-6 text-gold-400" style={{ fontFamily: '"Playfair Display", serif' }}>
              Our Story
            </h1>
            <p className="text-xl leading-relaxed text-cream-200">
              A journey of love, culture, and the timeless art of modest elegance
            </p>
            <div className="mt-6">
              <div className="w-20 h-1 bg-gold-500 mx-auto"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Story Section */}
      <section className="py-20 bg-cream-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
            <div className="relative w-full h-[500px] rounded-lg overflow-hidden shadow-2xl">
              <img
                src="https://public.readdy.ai/ai/img_res/3a2c58c688f473bf65c2a4bb2a25a277.jpg"
                alt="SheWearHouse Story"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/30 to-transparent"></div>
            </div>
            <div>
              <h2 className="text-4xl font-bold text-emerald-900 mb-6" style={{ fontFamily: '"Playfair Display", serif' }}>
                Born from Love & Tradition
              </h2>
              <p className="text-gray-700 mb-5 leading-relaxed text-base">
                SheWearHouse was born from a deep love of Islamic culture and the timeless art of modest dressing. 
                We believe that every Muslim woman and girl deserves to feel beautiful, confident, and connected to 
                her heritage through the clothes she wears.
              </p>
              <p className="text-gray-700 mb-5 leading-relaxed text-base">
                What began as a passion for handcrafted abayas has blossomed into a celebration of Islamic artistry. 
                Each piece in our collection tells a story—of skilled hands threading delicate beads, of fabrics chosen 
                for their grace and comfort, and of designs that honor both tradition and contemporary elegance.
              </p>
              <p className="text-gray-700 mb-5 leading-relaxed text-base">
                From our signature embroidered abayas to our handcrafted bangles and prayer beads, every item is 
                selected with care and reverence. We serve families across the globe, bringing the beauty of modest 
                Islamic wear to women and girls who value both faith and fashion.
              </p>
              <div className="mt-8 p-6 bg-gold-50 border-l-4 border-gold-500 rounded-r-lg">
                <p className="text-emerald-900 italic text-base leading-relaxed" style={{ fontFamily: '"Playfair Display", serif' }}>
                  "Modesty is not about hiding beauty—it's about revealing grace, dignity, and the strength of a woman who knows her worth."
                </p>
                <p className="text-gold-600 font-semibold mt-3 text-sm">— SheWearHouse Founder</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cultural Connection Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold text-emerald-900 mb-6" style={{ fontFamily: '"Playfair Display", serif' }}>
              Celebrating Islamic Heritage
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              Every stitch, every bead, every fold of fabric carries the legacy of Islamic artistry and the 
              timeless beauty of modest fashion
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-10 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="relative w-full h-72 rounded-lg overflow-hidden shadow-lg mb-6">
                <img
                  src="https://readdy.ai/api/search-image?query=extreme%20close-up%20macro%20photography%20of%20intricate%20gold%20thread%20embroidery%20and%20bead%20work%20on%20black%20abaya%20fabric%2C%20detailed%20Islamic%20geometric%20patterns%20and%20floral%20motifs%2C%20luxurious%20craftsmanship%2C%20rich%20textures%2C%20golden%20and%20emerald%20tones%2C%20artisan%20quality%2C%20no%20people%2C%20fabric%20detail%20shot&width=400&height=500&seq=cultural-embroidery-macro&orientation=portrait"
                  alt="Bead Embroidery Art"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <h3 className="text-xl font-bold text-emerald-900 mb-3" style={{ fontFamily: '"Playfair Display", serif' }}>
                The Art of Embroidery
              </h3>
              <p className="text-gray-700 leading-relaxed text-sm">
                Each embroidered abaya is a masterpiece of patience and skill. Gold threads and delicate beads 
                are woven into patterns inspired by Islamic art, creating wearable treasures that honor centuries 
                of tradition.
              </p>
            </div>

            <div className="text-center">
              <div className="relative w-full h-72 rounded-lg overflow-hidden shadow-lg mb-6">
                <img
                  src="https://readdy.ai/api/search-image?query=flowing%20elegant%20abaya%20fabric%20draped%20beautifully%20showing%20graceful%20silhouette%20and%20movement%2C%20back%20view%20with%20no%20face%20visible%2C%20soft%20natural%20lighting%2C%20peaceful%20Islamic%20aesthetic%2C%20emerald%20and%20cream%20tones%2C%20respectful%20modest%20fashion%20photography%2C%20cultural%20reverence%20and%20dignity&width=400&height=500&seq=cultural-modesty-draped&orientation=portrait"
                  alt="Modest Fashion Beauty"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <h3 className="text-xl font-bold text-emerald-900 mb-3" style={{ fontFamily: '"Playfair Display", serif' }}>
                Modesty as Empowerment
              </h3>
              <p className="text-gray-700 leading-relaxed text-sm">
                In Islamic culture, modest dress is not a limitation—it's a choice of dignity and strength. 
                Our abayas allow women to express their faith while embracing elegance, comfort, and personal style.
              </p>
            </div>

            <div className="text-center">
              <div className="relative w-full h-72 rounded-lg overflow-hidden shadow-lg mb-6">
                <img
                  src="https://readdy.ai/api/search-image?query=flat%20lay%20arrangement%20of%20handcrafted%20Islamic%20prayer%20beads%20tasbih%20and%20gold%20bangles%20on%20cream%20silk%20fabric%2C%20traditional%20Muslim%20accessories%20beautifully%20displayed%2C%20cultural%20craftsmanship%2C%20rich%20emerald%20and%20gold%20colors%2C%20artisan%20jewelry%20photography%2C%20elegant%20composition%2C%20no%20people&width=400&height=500&seq=cultural-accessories-flatlay&orientation=portrait"
                  alt="Handcrafted Accessories"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <h3 className="text-xl font-bold text-emerald-900 mb-3" style={{ fontFamily: '"Playfair Display", serif' }}>
                Handcrafted with Love
              </h3>
              <p className="text-gray-700 leading-relaxed text-sm">
                From prayer beads to bangles, our accessories are crafted by skilled artisans who pour their 
                hearts into every piece. These are not just adornments—they're symbols of faith, culture, and beauty.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Craftsmanship Section - 3 Pillars */}
      <section className="py-20 bg-gradient-to-b from-cream-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold text-emerald-900 mb-6" style={{ fontFamily: '"Playfair Display", serif' }}>
              Our Commitment to Excellence
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              Every piece from SheWearHouse is a testament to quality, artistry, and devotion
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white p-8 rounded-lg shadow-lg border-t-4 border-gold-500 text-center hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-6">
                <i className="ri-scissors-cut-line text-4xl text-emerald-700"></i>
              </div>
              <h3 className="text-xl font-bold text-emerald-900 mb-4" style={{ fontFamily: '"Playfair Display", serif' }}>
                Handpicked Fabrics
              </h3>
              <p className="text-gray-700 leading-relaxed text-sm">
                We source only the finest, softest fabrics that drape beautifully and feel luxurious against the skin. 
                Breathable, comfortable, and made to last—every material is chosen with care.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg border-t-4 border-gold-500 text-center hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-6">
                <i className="ri-sparkling-line text-4xl text-emerald-700"></i>
              </div>
              <h3 className="text-xl font-bold text-emerald-900 mb-4" style={{ fontFamily: '"Playfair Display", serif' }}>
                Bead & Thread Embroidery
              </h3>
              <p className="text-gray-700 leading-relaxed text-sm">
                Our embroidered abayas are works of art. Skilled artisans hand-stitch intricate patterns using gold 
                threads and delicate beads, creating designs that honor Islamic heritage and modern elegance.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg border-t-4 border-gold-500 text-center hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-6">
                <i className="ri-heart-3-line text-4xl text-emerald-700"></i>
              </div>
              <h3 className="text-xl font-bold text-emerald-900 mb-4" style={{ fontFamily: '"Playfair Display", serif' }}>
                Made with Love
              </h3>
              <p className="text-gray-700 leading-relaxed text-sm">
                Every abaya, every bangle, every prayer bead is selected and curated with love and reverence. 
                We treat each piece as if it were made for our own family—because you are part of ours.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-900 via-emerald-800 to-emerald-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C9A84C' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl font-bold text-gold-400 mb-6" style={{ fontFamily: '"Playfair Display", serif' }}>
            Discover Our Collection
          </h2>
          <p className="text-xl text-cream-200 mb-10 max-w-2xl mx-auto leading-relaxed">
            Explore our handpicked selection of signature abayas, handcrafted accessories, and modest wear 
            that celebrates your faith and style
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/clothing"
              className="bg-gold-500 text-emerald-900 hover:bg-gold-600 px-10 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer shadow-lg text-base"
            >
              Shop Signature Abayas
            </Link>
            <Link
              to="/accessories"
              className="bg-white text-emerald-900 hover:bg-cream-100 px-10 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer shadow-lg text-base"
            >
              Explore Accessories
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;